import { Cursors } from '../constants.js';
import { isUndefined, isString } from '../utils/functions.js';
import { Observable } from '../utils/Observable.js';
import { randomId } from '../math/randomId.js';
import '../math/Graph.js';
import '../math/Shapes.js';
import '@progress/kendo-drawing';
import { deepExtend } from '../utils/deep-extend.js';
import '../svg/Rotation.js';
import { Group } from '../svg/Group.js';
import { TextBlock } from '../svg/TextBlock.js';

const defaultOptions = {
    hover: {},
    cursor: Cursors.grip,
    content: {
        align: 'center middle'
    },
    selectable: true,
    serializable: true,
    enable: true
};
class DiagramElement extends Observable {
    constructor(options) {
        super();
        this.dataItem = (options || {}).dataItem;
        this.options = deepExtend({ id: randomId() }, defaultOptions, options);
        this.isSelected = false;
        this.visual = new Group({
            id: this.options.id,
            autoSize: this.options.autoSize
        });
        this.id = this.options.id;
        this._template();
    }
    _getCursor(point) {
        if (this.adorner) {
            return this.adorner._getCursor(point);
        }
        return this.options.cursor;
    }
    visible(value) {
        if (isUndefined(value)) {
            return this.visual.visible();
        }
        else {
            this.visual.visible(value);
        }
    }
    bounds(value) {
        if (isUndefined(value)) {
            return null;
        }
        return null;
    }
    refresh() {
        this.visual.redraw();
    }
    position(point) {
        this.options.x = point.x;
        this.options.y = point.y;
        this.visual.position(point);
    }
    toString() {
        return this.options.id;
    }
    serialize() {
        // the options json object describes the shape perfectly. So this object can serve as shape serialization.
        const json = deepExtend({}, { options: this.options });
        if (this.dataItem) {
            json.dataItem = this.dataItem.toString();
        }
        return json;
    }
    _content(content) {
        if (content !== undefined) {
            const options = this.options;
            if (isString(content)) {
                options.content.text = content;
            }
            else {
                deepExtend(options.content, content);
            }
            const contentOptions = options.content;
            const contentVisual = this._contentVisual;
            if (!contentVisual) {
                this._createContentVisual(contentOptions);
            }
            else {
                this._updateContentVisual(contentOptions);
            }
        }
        return this.options.content.text;
    }
    _createContentVisual(options) {
        if (options.text) {
            this._contentVisual = new TextBlock(options);
            this._contentVisual._includeInBBox = false;
            this.visual.append(this._contentVisual);
        }
    }
    _updateContentVisual(options) {
        this._contentVisual.redraw(options);
    }
    _hitTest(point) {
        const bounds = this.bounds();
        return this.visible() && bounds.contains(point) && this.options.enable;
    }
    _template() {
        if (this.options.content.template) {
            const data = this.dataItem || {}, elementTemplate = this.options.kendoTemplate(this.options.content.template, {
                paramName: 'dataItem'
            });
            this.options.content.text = elementTemplate(data);
        }
    }
    _canSelect() {
        return this.options.selectable !== false;
    }
    toJSON() {
        return {
            id: this.options.id
        };
    }
}

export { DiagramElement };
